import React, { useState, useEffect } from 'react'
import { X, Trash2, Upload, BookOpen, AlertCircle, Check, Loader2, ChevronDown, ChevronRight, FileText, Sparkles } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'

// Default prompt templates for AI generation
const DEFAULT_DESCRIPTION_PROMPT = `Generate a Book blurb for book "{title}" by {author} that will be added to my book store. up to 100 words.

Start with the hook: One strong sentence that tells the central conflict, question, or promise. It must make the reader curious. Describe the central tension without spoilers. Match the tone to the genre. Keep paragraphs short (2–4 lines). Use active, simple language. End with a cliffhanger or promise.

Check internet if needed.`

const DEFAULT_COVER_PROMPT = `Generate a realistic ebook cover for:

Book Title: {title}
Author: {author}

The cover should include the title "{title}" and author name "{author}" prominently displayed.
Create a book cover that clearly communicates the genre and appeals to the target audience, using a strong visual concept, appropriate mood, and readable typography. 
Include the title, and author name with clear hierarchy, and design the layout so it remains attractive and legible even as a small thumbnail in an online store. 
Deliver the design in high resolution following the required aspect ratio.
Use a color palette and illustration or photography style that match the book's tone from description below: 
{description}
`

interface Section {
  title: string
  href: string
  children: Section[]
  recommended: boolean
  estimated_pages?: number
}

interface BookSplitterProps {
  bookId: string
  bookTitle: string
  onClose: () => void
  onComplete: () => void
}

export const BookSplitter: React.FC<BookSplitterProps> = ({ bookId, bookTitle, onClose, onComplete }) => {
  const [loading, setLoading] = useState(true)
  const [sections, setSections] = useState<Section[]>([])
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set())
  const [coverReplacements, setCoverReplacements] = useState<Record<string, string>>({})
  const [defaultCoverUrl, setDefaultCoverUrl] = useState<string | null>(null)
  const [splitting, setSplitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    analyzBook()
  }, [bookId])

  const analyzBook = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiClient.analyzeBookForSplit(bookId)
      setSections(response.sections)
      setDefaultCoverUrl(response.cover_url)

      // Pre-select recommended sections
      const recommended = new Set<string>()
      response.sections.forEach((section) => {
        if (section.recommended) {
          recommended.add(section.title)
        }
      })
      setSelectedSections(recommended)
    } catch (err: any) {
      setError(err.message || 'Failed to analyze book')
      toast({
        title: 'Error',
        description: err.message || 'Failed to analyze book',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const toggleSection = (title: string) => {
    const newSelected = new Set(selectedSections)
    if (newSelected.has(title)) {
      newSelected.delete(title)
    } else {
      newSelected.add(title)
    }
    setSelectedSections(newSelected)
  }

  const handleCoverUpload = (title: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please upload an image file',
        variant: 'destructive',
      })
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const base64 = e.target?.result as string
      // Extract base64 content without data URL prefix
      const base64Content = base64.split(',')[1]
      setCoverReplacements({
        ...coverReplacements,
        [title]: base64Content,
      })
      toast({
        title: 'Cover updated',
        description: `Cover for "${title}" has been updated`,
      })
    }
    reader.readAsDataURL(file)
  }

  const removeCoverReplacement = (title: string) => {
    const newReplacements = { ...coverReplacements }
    delete newReplacements[title]
    setCoverReplacements(newReplacements)
  }

  const handleSplit = async (withAI: boolean = false) => {
    if (selectedSections.size === 0) {
      toast({
        title: 'No sections selected',
        description: 'Please select at least one section to extract',
        variant: 'destructive',
      })
      return
    }

    setSplitting(true)

    try {
      // Build array of sections to extract (with full data, including nested)
      const sectionsToExtract = collectSelectedSections(sections, selectedSections)

      const options = withAI ? {
        generateCover: true,
        generateDescription: true,
        promptTemplateCover: DEFAULT_COVER_PROMPT,
        promptTemplateDescription: DEFAULT_DESCRIPTION_PROMPT
      } : undefined

      const response = await apiClient.splitBook(bookId, sectionsToExtract, coverReplacements, options)

      toast({
        title: withAI ? 'Book split with AI successfully' : 'Book split successfully',
        description: `Created ${response.created_books.length} new books${withAI ? ' with AI-generated covers and descriptions' : ''}`,
      })

      onComplete()
      onClose()
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to split book',
        variant: 'destructive',
      })
    } finally {
      setSplitting(false)
    }
  }

  const getCoverForSection = (title: string): string | null => {
    // Use custom cover replacement if available (base64), otherwise use default cover URL
    if (coverReplacements[title]) {
      // Custom covers are stored as base64, convert to data URL
      return `data:image/jpeg;base64,${coverReplacements[title]}`
    }
    return defaultCoverUrl
  }

  // Recursively count all sections (including nested)
  const countAllSections = (sections: Section[]): number => {
    return sections.reduce((count, section) => {
      return count + 1 + (section.children ? countAllSections(section.children) : 0)
    }, 0)
  }

  // Recursively collect all selected sections (including nested)
  const collectSelectedSections = (sections: Section[], selected: Set<string>): Section[] => {
    const result: Section[] = []
    sections.forEach((section) => {
      if (selected.has(section.title)) {
        result.push(section)
      }
      if (section.children && section.children.length > 0) {
        result.push(...collectSelectedSections(section.children, selected))
      }
    })
    return result
  }

  return (
    <div className="book-splitter fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-2xl font-bold">Split Book into Sections</h2>
            <p className="text-sm text-gray-600 mt-1">{bookTitle}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={splitting}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && sections.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <BookOpen className="h-12 w-12 mb-4 opacity-50" />
              <p>No sections found in this book</p>
              <p className="text-sm mt-2">This book may not have a proper table of contents</p>
            </div>
          )}

          {!loading && !error && sections.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  {selectedSections.size} of {countAllSections(sections)} sections selected
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const allTitles = new Set<string>()
                      const collectTitles = (sects: Section[]) => {
                        sects.forEach((s) => {
                          allTitles.add(s.title)
                          if (s.children) collectTitles(s.children)
                        })
                      }
                      collectTitles(sections)
                      setSelectedSections(allTitles)
                    }}
                  >
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedSections(new Set())}>
                    Clear All
                  </Button>
                </div>
              </div>

              <SectionList
                sections={sections}
                selectedSections={selectedSections}
                toggleSection={toggleSection}
                getCoverForSection={getCoverForSection}
                coverReplacements={coverReplacements}
                handleCoverUpload={handleCoverUpload}
                removeCoverReplacement={removeCoverReplacement}
                level={0}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <p className="text-sm text-gray-600">
            {selectedSections.size > 0 && `${selectedSections.size} book${selectedSections.size !== 1 ? 's' : ''} will be created`}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={splitting}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handleSplit(false)} disabled={splitting || selectedSections.size === 0}>
              {splitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Splitting...
                </>
              ) : (
                `Split into ${selectedSections.size} Book${selectedSections.size !== 1 ? 's' : ''}`
              )}
            </Button>
            <Button onClick={() => handleSplit(true)} disabled={splitting || selectedSections.size === 0}>
              {splitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Splitting...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Split + AI
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

// Recursive component to render sections with nested children
interface SectionListProps {
  sections: Section[]
  selectedSections: Set<string>
  toggleSection: (title: string) => void
  getCoverForSection: (title: string) => string | null
  coverReplacements: Record<string, string>
  handleCoverUpload: (title: string, event: React.ChangeEvent<HTMLInputElement>) => void
  removeCoverReplacement: (title: string) => void
  level: number
}

const SectionList: React.FC<SectionListProps> = ({
  sections,
  selectedSections,
  toggleSection,
  getCoverForSection,
  coverReplacements,
  handleCoverUpload,
  removeCoverReplacement,
  level,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  const toggleExpanded = (title: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(title)) {
      newExpanded.delete(title)
    } else {
      newExpanded.add(title)
    }
    setExpandedSections(newExpanded)
  }

  return (
    <>
      {sections.map((section) => {
        const isSelected = selectedSections.has(section.title)
        const hasChildren = section.children && section.children.length > 0
        const isExpanded = expandedSections.has(section.title)
        const coverUrl = getCoverForSection(section.title)
        const hasCustomCover = coverReplacements[section.title] !== undefined

        return (
          <div key={section.title} style={{ marginLeft: `${level * 16}px` }}>
            <Card
              className={`section-card p-4 cursor-pointer transition-all ${
                isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => toggleSection(section.title)}
            >
              <div className="flex items-start gap-4">
                {/* Expand/Collapse button for sections with children */}
                {hasChildren && (
                  <div className="flex items-center h-6" onClick={(e) => toggleExpanded(section.title, e)}>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                    </Button>
                  </div>
                )}
                {!hasChildren && <div className="w-6" />}

                {/* Checkbox */}
                <div className="flex items-center h-6">
                  <div
                    className={`checkbox w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                </div>

                {/* Cover preview */}
                <div className="flex-shrink-0">
                  {coverUrl ? (
                    <div className="cover-preview relative">
                      <img
                        src={coverUrl}
                        alt={section.title}
                        className="w-16 h-24 object-cover rounded shadow"
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          e.currentTarget.style.display = 'none'
                          e.currentTarget.nextElementSibling?.classList.remove('hidden')
                        }}
                      />
                      <div className="hidden w-16 h-24 bg-gray-200 rounded flex items-center justify-center">
                        <BookOpen className="h-8 w-8 text-gray-400" />
                      </div>
                      {hasCustomCover && (
                        <div className="custom-cover-badge absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-16 h-24 bg-gray-200 rounded flex items-center justify-center">
                      <BookOpen className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Section info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate">{section.title}</h3>
                    {section.estimated_pages !== undefined && section.estimated_pages > 0 && (
                      <Badge 
                        variant={section.estimated_pages < 10 ? 'destructive' : 'secondary'} 
                        className="pages-badge shrink-0 text-xs"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        {section.estimated_pages} {section.estimated_pages === 1 ? 'page' : 'pages'}
                      </Badge>
                    )}
                  </div>
                  {hasChildren && (
                    <p className="text-sm text-gray-500 mt-1">
                      Contains {section.children.length} subsection{section.children.length !== 1 ? 's' : ''}
                    </p>
                  )}
                  {section.estimated_pages !== undefined && section.estimated_pages < 10 && section.estimated_pages > 0 && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Very short - consider not splitting into separate book
                    </p>
                  )}
                  {!section.recommended && (
                    <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      May not be a story (e.g., title page, colophon)
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {hasCustomCover ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCoverReplacement(section.title)}
                      title="Remove custom cover"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  ) : (
                    <label className="upload-cover-btn cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleCoverUpload(section.title, e)}
                      />
                      <Button variant="ghost" size="sm" asChild>
                        <span title="Upload custom cover">
                          <Upload className="h-4 w-4" />
                        </span>
                      </Button>
                    </label>
                  )}
                </div>
              </div>
            </Card>

            {/* Render nested children if expanded */}
            {hasChildren && isExpanded && (
              <div className="mt-2 ml-4 space-y-2 border-l-2 border-gray-200 pl-4">
                <SectionList
                  sections={section.children}
                  selectedSections={selectedSections}
                  toggleSection={toggleSection}
                  getCoverForSection={getCoverForSection}
                  coverReplacements={coverReplacements}
                  handleCoverUpload={handleCoverUpload}
                  removeCoverReplacement={removeCoverReplacement}
                  level={level + 1}
                />
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

